<template>
  <div>
    <b-modal
      id="modal-sop-validation"
      @shown="initializeFromExisting(existingValidations)"
      centered
      size="lg"
      scrollable
      hide-header-close
      modal-class="mc-validasi-modal"
      body-class="mc-validasi-body"
    >
      <!-- ================= HEADER ================= -->
      <template #modal-header>
        <div class="mc-vhead">
          <div class="mc-vhead__title">
            {{ $t('task.column.sop') }} : <strong>{{ licensePlate || 'No License Plate' }}</strong>
          </div>
          <div class="mc-vhead__meta">
            {{ workOrder.wo_number }}
            <span v-if="workOrder.customer_name"> · {{ workOrder.customer_name }}</span>
          </div>
          <b-button
            variant="link"
            class="mc-vhead__close"
            @click="$bvModal.hide('modal-sop-validation')"
          >
            <i class="icon-outline_close" />
          </b-button>
        </div>
      </template>

      <!-- ================= RINGKASAN / PROGRESS ================= -->
      <div
        v-if="
          hasAccessAll([
            'sms-sop-validation:create',
            'sms-sop-validation:update',
            'sms-training-dataset:create',
            'sms-training-dataset:update',
          ])
        "
        class="mc-sticky-header"
      >
        <div class="mc-vsummary">
          <span class="mc-chip mc-chip--valid">
            <i class="icon-fill_checkmark-circle" /> {{ sopValidCount }} valid
          </span>
          <span class="mc-chip mc-chip--invalid">
            <i class="icon-fill_close-circle" /> {{ sopInvalidCount }} tidak valid
          </span>
          <span class="mc-chip mc-chip--muted">{{ sopPendingCount }} belum</span>

          <div class="mc-vsummary__progress">
            <span class="mc-vsummary__label">
              {{ sopValidCount + sopInvalidCount }} dari {{ totalValidatableCount }} foto divalidasi
            </span>
            <b-progress
              :value="sopValidCount + sopInvalidCount"
              :max="totalValidatableCount || 1"
              height="6px"
              class="mc-vsummary__bar"
            />
          </div>
        </div>
      </div>

      <!-- ================= DAFTAR ITEM SOP ================= -->
      <div v-if="sopItems.length === 0" style="padding: 20px; text-align: center; color: #999">
        <p>Tidak ada data SOP</p>
      </div>

      <div
        v-for="item in sopItems"
        :key="item.tc_id || item.checklist_id"
        class="mc-photocard"
        :class="{
          'is-valid': photoValidation[item.tc_id] === 'valid',
          'is-invalid': photoValidation[item.tc_id] === 'invalid',
        }"
      >
        <!-- ===== TITLE ROW (SELALU DI ATAS FOTO) ===== -->
        <div class="mc-photocard__title-row">
          <div class="mc-photocard__name">{{ item.checklist_name }}</div>
          <svg
            v-if="photoValidation[item.tc_id] === 'valid'"
            class="mc-svg-icon mc-svg-icon--status"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" fill="#16a34a" />
            <path
              d="M8 12.5l2.5 2.5L16 9.5"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
          <svg
            v-else-if="photoValidation[item.tc_id] === 'invalid'"
            class="mc-svg-icon mc-svg-icon--status"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" fill="#dc2626" />
            <path
              d="M9 9l6 6M15 9l-6 6"
              stroke="#ffffff"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              fill="none"
            />
          </svg>
        </div>

        <!-- ===== MEDIA (HANYA JIKA ADA FOTO ATAU TIDAK PUNYA DETAIL) ===== -->
        <div
          v-if="hasPhoto(item) || !hasDetail(item)"
          class="mc-photocard__media"
          :class="{ 'is-clickable': hasPhoto(item) }"
          @click="hasPhoto(item) && openPreview(getImageUrl(item), item.checklist_name)"
        >
          <video
            v-if="hasPhoto(item) && isMediaVideo(getImageUrl(item))"
            :src="getImageUrl(item)"
            class="mc-photocard__video"
            muted
          />
          <img v-else-if="hasPhoto(item)" :src="getImageUrl(item)" :alt="item.checklist_name" />
          <div v-else class="mc-photocard__empty">
            <i class="icon-outline_image" />
            <span>Klik untuk memperbesar</span>
          </div>

          <button
            v-if="hasPhoto(item)"
            type="button"
            class="mc-photocard__zoom-btn"
            @click.stop="openPreview(getImageUrl(item), item.checklist_name)"
          >
            <i
              :class="isMediaVideo(getImageUrl(item)) ? 'icon-outline_play' : 'icon-outline_search'"
            />
          </button>
        </div>

        <!-- ===== FOOT CARD SOP (AKSI & ALASAN) ===== -->
        <div class="mc-photocard__foot">
          <div class="mc-photocard__actions">
            <b-button
              v-if="hasDetail(item)"
              v-b-toggle="`collapse-detail-${item.tc_id}`"
              size="sm"
              pill
              variant="outline-secondary"
            >
              <i class="icon-outline_chevron-down" /> Detail SOP ({{ getDetailCount(item) }})
            </b-button>

            <template v-if="hasPhoto(item) && isValidationRequired(item) && hasAccessAll(['sms-training-dataset:read', 'sms-sop-validation:read'])">
              <b-button
                size="sm"
                pill
                :variant="photoValidation[item.tc_id] === 'valid' ? 'success' : 'outline-success'"
                @click="setPhotoValidation(item.tc_id, 'valid')"
              >
                <i class="icon-outline_checkmark-circle" /> Valid
              </b-button>
              <b-button
                size="sm"
                pill
                :variant="photoValidation[item.tc_id] === 'invalid' ? 'danger' : 'outline-danger'"
                @click="setPhotoValidation(item.tc_id, 'invalid')"
              >
                <i class="icon-outline_close-circle" /> Tidak valid
              </b-button>
            </template>
          </div>

          <!-- Section Alasan Tidak Valid (Foto SOP Utama) -->
          <div
            v-if="isValidationRequired(item) && photoValidation[item.tc_id] === 'invalid'"
            class="mc-photocard__reasons mt-3 p-3 rounded"
          >
            <div class="d-flex align-items-center justify-content-between mb-2">
              <span class="mc-reasons__label font-weight-bold text-danger">
                Alasan tidak valid *
                <small class="text-muted font-weight-normal">(pilih 1 alasan)</small>
              </span>
              <span v-if="photoInvalidReasons[item.tc_id]" class="mc-reasons__counter">
                1 alasan dipilih
              </span>
            </div>

            <div class="d-flex flex-wrap gap-2">
              <b-button
                v-for="reason in invalidReasonsList"
                :key="reason.invalid_reason_id"
                size="sm"
                pill
                :variant="
                  photoInvalidReasons[item.tc_id] === reason.invalid_reason_id ? 'danger' : 'light'
                "
                class="mc-reason-chip"
                @click="selectPhotoReason(item.tc_id, reason.invalid_reason_id)"
              >
                {{ reason.name }}
              </b-button>
            </div>
          </div>
        </div>

        <!-- ===== COLLAPSE DETAIL ===== -->
        <b-collapse :id="`collapse-detail-${item.tc_id}`" class="mt-3" :visible="true">
          <div class="p-3 bg-light rounded border">
            <div v-if="isMigrationCase">
              <component
                :is="getMigrationComponent(item)"
                :item="item"
                :workOrder="workOrder"
                :selectedTask="selectedTask"
              />
            </div>

            <!-- Temuan tindakan -->
            <ModalFindingAction
              v-if="item.flag === 'TemuanTindakan'"
              :item="item"
              :map-component="mapComponent"
            />

            <div v-else-if="item.tasks_checklist_detail && item.tasks_checklist_detail.length > 0">
              <div
                v-for="(detail, idx) in item.tasks_checklist_detail"
                :key="detail.tcd_id || detail.checklist_detail_id || idx"
                class="mc-detailcard"
                :class="{
                  'is-valid':
                    getDetailImageUrl(detail) &&
                    detailValidation[detail.tcd_id] === 'valid',
                  'is-invalid':
                    getDetailImageUrl(detail) &&
                    detailValidation[detail.tcd_id] === 'invalid',
                }"
              >
                <!-- ===== HEADER DETAIL (JUDUL DI ATAS FOTO) ===== -->
                <div class="mc-detailcard__header">
                  <span class="font-weight-bold text-dark">{{ getDetailTitle(detail) }}</span>
                  <template
                    v-if="
                      getDetailImageUrl(detail) &&
                      isDetailValidationRequired(detail)
                    "
                  >
                    <svg
                      v-if="detailValidation[detail.tcd_id] === 'valid'"
                      class="mc-svg-icon mc-svg-icon--status"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" fill="#16a34a" />
                      <path
                        d="M8 12.5l2.5 2.5L16 9.5"
                        stroke="#ffffff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        fill="none"
                      />
                    </svg>
                    <svg
                      v-else-if="detailValidation[detail.tcd_id] === 'invalid'"
                      class="mc-svg-icon mc-svg-icon--status"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="10" fill="#dc2626" />
                      <path
                        d="M9 9l6 6M15 9l-6 6"
                        stroke="#ffffff"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        fill="none"
                      />
                    </svg>
                  </template>
                </div>

                <!-- ===== MEDIA DETAIL ===== -->
                <div
                  v-if="getDetailImageUrl(detail)"
                  class="mc-detailcard__media is-clickable"
                  @click="openPreview(getDetailImageUrl(detail), getDetailTitle(detail))"
                >
                  <video
                    v-if="isMediaVideo(getDetailImageUrl(detail))"
                    :src="getDetailImageUrl(detail)"
                    class="mc-photocard__video"
                    muted
                  />
                  <img v-else :src="getDetailImageUrl(detail)" :alt="getDetailTitle(detail)" />
                  <button
                    type="button"
                    class="mc-photocard__zoom-btn mc-photocard__zoom-btn--sm"
                    @click.stop="openPreview(getDetailImageUrl(detail), getDetailTitle(detail))"
                  >
                    <i
                      :class="
                        isMediaVideo(getDetailImageUrl(detail))
                          ? 'icon-outline_play'
                          : 'icon-outline_search'
                      "
                    />
                  </button>
                </div>

                <!-- ===== CONTENT DETAIL ===== -->
                <div class="mc-detailcard__content">
                  <ChecklistDetailCustomProfile
                    v-if="item.flag === 'CustomProfile'"
                    :task-data="selectedTask"
                    :work-order="workOrder"
                    :checklist="item"
                  />
                  <div v-if="detail.note" class="mc-detailcard__note">
                    <strong>Catatan:</strong> {{ detail.note }}
                  </div>

                  <div
                    v-if="getDetailImageUrl(detail) && isDetailValidationRequired(detail) && hasAccessAll(['sms-training-dataset:read', 'sms-sop-validation:read'])"
                    class="mc-detailcard__actions"
                  >
                    <b-button
                      size="sm"
                      pill
                      :variant="
                        detailValidation[detail.tcd_id] === 'valid'
                          ? 'success'
                          : 'outline-success'
                      "
                      @click="setDetailValidation(detail.tcd_id, 'valid')"
                    >
                      <i class="icon-outline_checkmark-circle" /> Valid
                    </b-button>
                    <b-button
                      size="sm"
                      pill
                      :variant="
                        detailValidation[detail.tcd_id] === 'invalid'
                          ? 'danger'
                          : 'outline-danger'
                      "
                      @click="setDetailValidation(detail.tcd_id, 'invalid')"
                    >
                      <i class="icon-outline_close-circle" /> Tidak valid
                    </b-button>
                  </div>

                  <div
                    v-if="
                      isDetailValidationRequired(detail) &&
                      detailValidation[detail.tcd_id] === 'invalid'
                    "
                    class="mc-photocard__reasons mt-3 p-3 rounded"
                  >
                    <div class="d-flex align-items-center justify-content-between mb-2">
                      <span class="mc-reasons__label font-weight-bold text-danger">
                        Alasan tidak valid *
                        <small class="text-muted font-weight-normal">(pilih 1 alasan)</small>
                      </span>
                      <span
                        v-if="detailInvalidReasons[detail.tcd_id]"
                        class="mc-reasons__counter"
                      >
                        1 alasan dipilih
                      </span>
                    </div>

                    <div class="d-flex flex-wrap gap-2">
                      <b-button
                        v-for="reason in invalidReasonsList"
                        :key="reason.invalid_reason_id"
                        size="sm"
                        pill
                        :variant="
                          detailInvalidReasons[detail.tcd_id] === reason.invalid_reason_id
                            ? 'danger'
                            : 'light'
                        "
                        class="mc-reason-chip"
                        @click="selectDetailReason(detail.tcd_id, reason.invalid_reason_id)"
                      >
                        {{ reason.name }}
                      </b-button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              <slot :name="`checklist-${item.tc_id}`" />
            </div>
          </div>
        </b-collapse>
      </div>

      <!-- ================= FOOTER ================= -->
      <template #modal-footer>
        <div class="mc-vfoot">
          <div class="mc-vfoot__info">
            <span
              v-if="
                hasAccessAll([
                  'sms-sop-validation:create',
                  'sms-sop-validation:update',
                  'sms-training-dataset:create',
                  'sms-training-dataset:update',
                ])
              "
              class="mc-vfoot__validator"
            >
              Validator: <strong>{{ validatorName }}</strong>
            </span>
          </div>
          <div class="mc-vfoot__actions">
            <b-button
              pill
              variant="outline-secondary"
              @click="$bvModal.hide('modal-sop-validation')"
            >
              {{ $t('global.close') }}
            </b-button>
            <b-button
              v-if="
                hasAccessAll([
                  'sms-sop-validation:create',
                  'sms-sop-validation:update',
                  'sms-training-dataset:create',
                  'sms-training-dataset:update',
                ])
              "
              pill
              variant="primary"
              :disabled="!canSaveValidation"
              @click="onSaveValidation"
            >
              Simpan validasi
            </b-button>
          </div>
        </div>
      </template>

      <b-modal
        id="modal-sop-preview"
        centered
        size="xl"
        hide-footer
        hide-header
        modal-class="mc-preview-modal"
        @hidden="resetZoom"
      >
        <div
          class="mc-preview-stage"
          :class="{ 'is-draggable': zoomLevel > 1, 'is-dragging': isDragging }"
          @wheel.prevent="onPreviewWheel"
          @mousedown="onPreviewDragStart"
          @mousemove="onPreviewDragMove"
          @mouseup="onPreviewDragEnd"
          @mouseleave="onPreviewDragEnd"
          @dblclick="resetZoom"
        >
          <!-- Close + Judul sejajar -->
          <div class="mc-preview-head">
            <button
              type="button"
              class="mc-preview-close"
              @click="$bvModal.hide('modal-sop-preview')"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
            <div class="mc-preview-title">{{ previewTitle }}</div>
          </div>

          <!-- Media -->
          <video
            v-if="isPreviewVideo"
            :src="previewUrl"
            controls
            autoplay
            class="mc-preview-media mc-preview-media--video"
            :style="previewMediaStyle"
          />
          <img
            v-else
            :src="previewUrl"
            :alt="previewTitle"
            class="mc-preview-media"
            :style="previewMediaStyle"
            draggable="false"
          />

          <!-- Floating zoom -->
          <div class="mc-preview-zoom" @mousedown.stop @dblclick.stop>
            <button
              type="button"
              class="mc-preview-zoom__btn"
              :disabled="zoomLevel <= 0.5"
              @click="zoomOut"
            >
              −
            </button>
            <span class="mc-preview-zoom__value">{{ Math.round(zoomLevel * 100) }}%</span>
            <button
              type="button"
              class="mc-preview-zoom__btn"
              :disabled="zoomLevel >= 5"
              @click="zoomIn"
            >
              +
            </button>
            <button
              type="button"
              class="mc-preview-zoom__btn mc-preview-zoom__btn--reset"
              :disabled="zoomLevel === 1 && panX === 0 && panY === 0"
              @click="resetZoom"
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M3 12a9 9 0 1 0 3-6.7" />
                <path d="M3 4v5h5" />
              </svg>
            </button>
          </div>
        </div>
      </b-modal>
    </b-modal>
  </div>
</template>

<script>
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'

import FormDeviceSetToCustomer from './components/FormDeviceSetToCustomer.vue'
import FormCreateDevice from './components/FormCreateDevice.vue'
import ModalFindingAction from './components/ModalFindingAction.vue'
import ChecklistDetailCustomProfile from './components/ChecklistDetailCustomProfile.vue'

export default {
  name: 'ModalSOPValidation',

  components: {
    FormDeviceSetToCustomer,
    FormCreateDevice,
    ModalFindingAction,
    ChecklistDetailCustomProfile,
  },

  mixins: [Helper],

  props: {
    workOrder: {
      type: Object,
      default: () => ({}),
    },
    selectedTask: {
      type: [Object, String],
      default: () => ({}),
    },
    licensePlate: {
      type: String,
      default: '',
    },
    tasksId: {
      type: [Number, String],
      default: null,
    },
    isCheck: {
      type: Boolean,
      default: false,
    },
    SOP: {
      type: Function,
      required: true,
    },
    invalidReasonsList: {
      type: Array,
      default: () => [],
    },
    existingValidations: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      photoValidation: {},
      detailValidation: {},
      typeValidation: null,
      previewUrl: '',
      previewTitle: '',
      photoInvalidReasons: {},
      detailInvalidReasons: {},

      // ZOOM STATE
      zoomLevel: 1,
      panX: 0,
      panY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
    }
  },

  watch: {
    existingValidations: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.initializeFromExisting(newVal)
      },
    },
  },

  computed: {
    isMigrationCase() {
      return Boolean(this.workOrder?.is_migrate)
    },

    sopItems() {
      try {
        console.log('[sopItems] tasksId =', this.tasksId, 'isCheck =', this.isCheck)
        const fromProp = this.SOP(this.tasksId, this.isCheck)
        console.log('[sopItems] result length =', fromProp?.length)
        if (fromProp && fromProp.length > 0) return fromProp

        // Fallback dengan filter sesuai tipe WO
        const task = typeof this.selectedTask === 'object' ? this.selectedTask : {}
        if (task && task.task_checklist && task.task_checklist.length > 0) {
          let checklist = task.task_checklist
          if (this.workOrder && this.workOrder.wo_type === 'maintenance') {
            if (this.isCheck) {
              // Hanya yang mengandung 'H'
              return checklist.filter((c) => c.wo_type && c.wo_type.some((t) => t.includes('H')))
            } else {
              // Hanya yang TIDAK mengandung 'H'
              return checklist.filter((c) => c.wo_type && c.wo_type.some((t) => !t.includes('H')))
            }
          }
          return checklist // install / move -> semua
        }
        return []
      } catch (e) {
        return []
      }
    },

    // Mengumpulkan HANYA detail checklist yang MEMILIKI MEDIA & WAJIB DIVALIDASI
    allDetails() {
      const details = []
      this.sopItems.forEach((item) => {
        if (item.tasks_checklist_detail && item.tasks_checklist_detail.length > 0) {
          item.tasks_checklist_detail.forEach((d) => {
            if (this.getDetailImageUrl(d) && this.isDetailValidationRequired(d)
            ) {
              details.push(d)
            }
          })
        }
      })
      return details
    },

    // Hanya mengambil SOP induk yang MEMILIKI FOTO & WAJIB DIVALIDASI
    validatableSopItems() {
      return this.sopItems.filter((item) => this.hasPhoto(item) && this.isValidationRequired(item))
    },

    totalValidatableCount() {
      return this.validatableSopItems.length + this.allDetails.length
    },

    sopValidCount() {
      const mainValid = this.validatableSopItems.filter(
        (i) => this.photoValidation[i.tc_id] === 'valid'
      ).length
      const detailValid = this.allDetails.filter(
        (d) => this.detailValidation[d.tcd_id] === 'valid'
      ).length
      return mainValid + detailValid
    },

    sopInvalidCount() {
      const mainInvalid = this.validatableSopItems.filter(
        (i) => this.photoValidation[i.tc_id] === 'invalid'
      ).length
      const detailInvalid = this.allDetails.filter(
        (d) => this.detailValidation[d.tcd_id] === 'invalid'
      ).length
      return mainInvalid + detailInvalid
    },

    sopPendingCount() {
      return this.totalValidatableCount - this.sopValidCount - this.sopInvalidCount
    },

    vehicleTypeLabel() {
      const task =
        typeof this.selectedTask === 'object' && this.selectedTask !== null ? this.selectedTask : {}

      if (task.manufacturerModelType) return task.manufacturerModelType
      if (task.manufacturerModel) return task.manufacturerModel

      const getVal = (val) => {
        if (!val) return ''
        if (typeof val === 'string') return val.trim()
        if (typeof val === 'object') return val.name || val.label || val.title || ''
        return ''
      }

      const vt =
        task?.vehicleType ||
        task?.device?.vehicle?.vehicle_type ||
        task?.vehicle?.vehicle_type ||
        task?.vehicle_type ||
        task?.vehicle ||
        {}

      const manufacturer = getVal(vt.manufacturer || task.manufacturer)
      const model = getVal(vt.model_type || vt.model || task.model_type || task.model)
      const type = getVal(vt.type || task.type)

      const modelType = [model, type].filter(Boolean).join(' ')

      if (manufacturer && modelType) {
        return `${manufacturer} - ${modelType}`
      }

      return manufacturer || modelType || '-'
    },

    validatorName() {
      return this.getUserName()
    },

    isPreviewVideo() {
      return this.isMediaVideo(this.previewUrl)
    },

    canSaveValidation() {
      if (this.sopPendingCount > 0) return false

      const invalidPhotosHaveReason = this.validatableSopItems
        .filter((item) => this.photoValidation[item.tc_id] === 'invalid')
        .every((item) => Boolean(this.photoInvalidReasons[item.tc_id]))

      const invalidDetailsHaveReason = this.allDetails
        .filter((detail) => this.detailValidation[detail.tcd_id] === 'invalid')
        .every((detail) => Boolean(this.detailInvalidReasons[detail.tcd_id]))

      return invalidPhotosHaveReason && invalidDetailsHaveReason
    },

    bulkPayload() {
      const items = []

      // === Bangun lookup map dari existingValidations ===
      // Key: `${table_relation}-${relation_id}` -> training_dataset_id
      const existingMap = {}
      if (Array.isArray(this.existingValidations)) {
        this.existingValidations.forEach((v) => {
          if (!v) return
          const relId = v.relation_id != null ? Number(v.relation_id) : null
          const tableRel = v.table_relation
          if (relId != null && tableRel) {
            existingMap[`${tableRel}-${relId}`] = v.training_dataset_id ?? v.id ?? null
          }
        })
      }

      const getExistingId = (tableRelation, relationId) => {
        if (relationId == null) return null
        return existingMap[`${tableRelation}-${Number(relationId)}`] ?? null
      }
      // =================================================

      // 1. SOP utama (hanya yang divalidasi)
      this.validatableSopItems.forEach((item) => {
        const fullUrl = this.getImageUrl(item)
        if (!fullUrl) return
        const status = this.photoValidation[item.tc_id]
        if (!status) return

        const url = this.extractPathFromUrl(fullUrl)
        const relationId = item.tc_id || this.tasksId

        const payloadItem = {
          name: this.vehicleTypeLabel,
          category: item.checklist_name,
          url: url,
          table_relation: 'tasks_checklist',
          relation_id: relationId,
          state: status,
          validate_at: new Date(),
          invalid_reason_id: status === 'invalid' ? this.photoInvalidReasons[item.tc_id] : null,
          is_trained: false,
          trained_at: null,
          wo_id: this.workOrder.wo_id,
        }

        // Tambahkan training_dataset_id kalau sudah ada data existing
        const existingId = getExistingId('tasks_checklist', relationId)
        if (existingId) {
          payloadItem.training_dataset_id = existingId
        }

        items.push(payloadItem)
      })

      // 2. Sub-detail SOP (hanya yang divalidasi)
      this.allDetails.forEach((detail) => {
        const fullUrl = this.getDetailImageUrl(detail)
        if (!fullUrl) return
        const status = this.detailValidation[detail.tcd_id]
        if (!status) return

        const url = this.extractPathFromUrl(fullUrl)
        const relationId = detail.tcd_id || this.tasksId

        const payloadItem = {
          name: this.vehicleTypeLabel,
          category: this.getDetailTitle(detail),
          url: url,
          table_relation: 'tasks_checklist_detail',
          relation_id: relationId,
          state: status,
          validate_at: new Date(),
          invalid_reason_id: status === 'invalid' ? this.detailInvalidReasons[detail.tcd_id] : null,
          is_trained: false,
          trained_at: null,
          wo_id: this.workOrder.wo_id,
        }

        // Tambahkan training_dataset_id kalau sudah ada data existing
        const existingId = getExistingId('tasks_checklist_detail', relationId)
        if (existingId) {
          payloadItem.training_dataset_id = existingId
        }

        items.push(payloadItem)
      })

      return items
    },

    previewMediaStyle() {
      return {
        transform: `translate(${this.panX}px, ${this.panY}px) scale(${this.zoomLevel})`,
        transition: this.isDragging ? 'none' : 'transform 0.15s ease',
        cursor: this.zoomLevel > 1 ? (this.isDragging ? 'grabbing' : 'grab') : 'default',
      }
    },

    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },

  methods: {
    isValidationRequired(item) {
      // Jika item memiliki properti is_validation_required, gunakan nilainya.
      // Jika tidak ada, anggap perlu divalidasi jika item memiliki foto.
      if (item && 'is_validation_required' in item) {
        return Boolean(item.is_validation_required)
      }
      // Default: jika ada foto, anggap perlu divalidasi
      return this.hasPhoto(item)
    },

    isDetailValidationRequired(detail) {
      if (detail && 'is_validation_required' in detail) {
        return Boolean(detail.is_validation_required)
      }
      return !!this.getDetailImageUrl(detail)
    },

    hasDetail(item) {
      return Boolean(
        (item.tasks_checklist_detail && item.tasks_checklist_detail.length > 0) ||
          this.isMigrationCase
      )
    },

    getDetailCount(item) {
      return item.tasks_checklist_detail ? item.tasks_checklist_detail.length : 0
    },

    getImageUrl(item) {
      if (!item) return null
      return item.url || item.photo || null
    },

    getDetailImageUrl(detail) {
      if (!detail) return null
      return detail.url || detail.photo || null
    },

    getDetailTitle(detail) {
      if (!detail) return ''
      return detail.description || detail.checklist_name || detail.name || 'Detail Item'
    },

    hasPhoto(item) {
      return !!this.getImageUrl(item)
    },

    isMediaVideo(url) {
      if (!url) return false
      return url.includes('.mp4') || url.includes('.webm') || url.includes('.ogg')
    },

    openPreview(url, title) {
      if (!url) return
      this.resetZoom()
      this.previewUrl = url
      this.previewTitle = title || 'Preview Media'
      this.$bvModal.show('modal-sop-preview') // <-- beda di sini
    },

    zoomIn() {
      this.zoomLevel = Math.min(+(this.zoomLevel + 0.25).toFixed(2), 5)
    },

    zoomOut() {
      this.zoomLevel = Math.max(+(this.zoomLevel - 0.25).toFixed(2), 0.5)
      if (this.zoomLevel <= 1) {
        this.panX = 0
        this.panY = 0
      }
    },

    resetZoom() {
      this.zoomLevel = 1
      this.panX = 0
      this.panY = 0
      this.isDragging = false
    },

    onPreviewWheel(e) {
      if (this.zoomLevel === 1 && e.deltaY > 0) return
      if (e.deltaY < 0) this.zoomIn()
      else this.zoomOut()
    },

    onPreviewDragStart(e) {
      if (this.zoomLevel <= 1 || e.button !== 0) return
      this.isDragging = true
      this.dragStartX = e.clientX - this.panX
      this.dragStartY = e.clientY - this.panY
    },

    onPreviewDragMove(e) {
      if (!this.isDragging) return
      this.panX = e.clientX - this.dragStartX
      this.panY = e.clientY - this.dragStartY
    },

    onPreviewDragEnd() {
      this.isDragging = false
    },

    getMigrationComponent(item) {
      if (item.flag === 'DeviceSetToCustomer' || item.checklist_name?.includes('Customer')) {
        return 'FormDeviceSetToCustomer'
      }
      return 'FormCreateDevice'
    },

    setPhotoValidation(tcId, value) {
      this.$set(this.photoValidation, tcId, value)
      if (value !== 'invalid') {
        this.$set(this.photoInvalidReasons, tcId, null)
      }
    },

    setDetailValidation(tcdId, value) {
      this.$set(this.detailValidation, tcdId, value)
      if (value !== 'invalid') {
        this.$set(this.detailInvalidReasons, tcdId, null)
      }
    },

    selectPhotoReason(tcId, reasonId) {
      if (this.photoInvalidReasons[tcId] === reasonId) {
        this.$set(this.photoInvalidReasons, tcId, null)
      } else {
        this.$set(this.photoInvalidReasons, tcId, reasonId)
      }
    },

    selectDetailReason(tcdId, reasonId) {
      if (this.detailInvalidReasons[tcdId] === reasonId) {
        this.$set(this.detailInvalidReasons, tcdId, null)
      } else {
        this.$set(this.detailInvalidReasons, tcdId, reasonId)
      }
    },

    onSaveValidation() {
      this.$emit('save', {
        items: this.bulkPayload,
      })
      this.$bvModal.hide('modal-sop-validation')
    },

    initializeFromExisting(validations) {
      this.resetValidation()
      if (!validations) return

      validations.forEach((item) => {
        const relationId = Number(item.relation_id) // konversi ke number
        const state = item.state
        const reason = item.invalid_reason_id

        if (item.table_relation === 'tasks_checklist') {
          this.$set(this.photoValidation, relationId, state)
          if (state === 'invalid' && reason) {
            this.$set(this.photoInvalidReasons, relationId, reason)
          }
        } else if (item.table_relation === 'tasks_checklist_detail') {
          this.$set(this.detailValidation, relationId, state)
          if (state === 'invalid' && reason) {
            this.$set(this.detailInvalidReasons, relationId, reason)
          }
        }
      })
    },

    resetValidation() {
      this.photoValidation = {}
      this.detailValidation = {}
      this.photoInvalidReasons = {}
      this.detailInvalidReasons = {}
      this.typeValidation = null
    },

    extractPathFromUrl(url) {
      if (!url) return null
      try {
        const urlObj = new URL(url)
        let pathname = urlObj.pathname
        if (pathname.startsWith('/')) pathname = pathname.substring(1)
        return pathname
      } catch (e) {
        const match = url.match(/\/(upload\/[^?]+)/)
        return match ? match[1] : url
      }
    },
  },
}
</script>

<style lang="scss">
/* ---------- Modal shell ---------- */
.mc-validasi-modal .modal-content {
  border: 0;
  border-radius: 12px;
  overflow: hidden;
}
.mc-validasi-modal .modal-header {
  background: #123a86;
  color: #fff;
  padding: 0;
  border: 0;
}
.mc-validasi-body {
  background: #f6f8fb;
  padding: 16px 20px;
}

/* ---------- Header ---------- */
.mc-vhead {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}
.mc-vhead__title {
  font-size: 16px;
}
.mc-vhead__meta {
  margin-left: auto;
  font-size: 13px;
  opacity: 0.85;
  text-align: right;
}
.mc-vhead__close {
  color: #fff !important;
  padding: 0 4px;
  font-size: 20px;
}

/* ---------- Summary ---------- */
.mc-vsummary {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 14px;
}
.mc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
}
.mc-chip--valid {
  color: #0f7a4d;
  background: #e6f6ee;
}
.mc-chip--invalid {
  color: #c0392b;
  background: #fdecea;
}
.mc-chip--muted {
  color: #6b7280;
  background: #f1f3f7;
}
.mc-vsummary__progress {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 240px;
}
.mc-vsummary__label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}
.mc-vsummary__bar {
  flex: 1;
  background: #e5e9f2;
}

/* ---------- Tipe kendaraan ---------- */
.mc-vtype {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #eef4ff;
  border: 1px solid #dbe6fb;
  border-radius: 10px;
  padding: 12px 16px;
  margin-bottom: 14px;
}
.mc-vtype__icon {
  font-size: 22px;
  color: #123a86;
}
.mc-vtype__caption {
  font-size: 12px;
  color: #6b7280;
}
.mc-vtype__value {
  font-size: 15px;
  font-weight: 700;
  color: #0f2a5c;
}
.mc-vtype__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

/* ---------- Kartu foto utama ---------- */
.mc-photocard {
  background: #fff;
  border: 1px solid #e5e9f2;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 14px;
  transition: border-color 0.15s ease;
}
.mc-photocard.is-valid {
  border-color: #22a06b;
}
.mc-photocard.is-invalid {
  border-color: #e05a4d;
}
.mc-photocard__media {
  position: relative;
  background: #e6e9f0;
  border-radius: 8px;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &.is-clickable {
    cursor: pointer;
  }
}
.mc-photocard__media img,
.mc-photocard__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.mc-photocard__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #8a93a5;
  font-size: 13px;
}
.mc-photocard__empty i {
  font-size: 28px;
}

.mc-detailcard__media {
  position: relative;
  width: 100%; /* <-- dari 140px */
  height: 180px; /* <-- dari 100px */
  background: #e6e9f0;
  border-radius: 6px;
  overflow: hidden;
  /* hapus: flex-shrink: 0 */

  &.is-clickable {
    cursor: pointer;
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: contain; /* <-- dari cover */
  }

  .mc-photocard__zoom-btn {
    top: 6px;
    right: 6px;
    padding: 4px 8px;
    font-size: 10px;
  }
}

.mc-detailcard__note {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

/* ---------- Footer ---------- */
.mc-validasi-modal .modal-footer {
  background: #fff;
  border-top: 1px solid #e5e9f2;
}

.mc-vfoot__info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  color: #6b7280;
}
.mc-vfoot__badge {
  border: 1px solid #e5e9f2;
}
.mc-vfoot__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
}

/* ---------- Modal Preview Media (Lightbox) ---------- */
.mc-preview-modal .modal-content {
  border-radius: 12px;
  overflow: hidden;
  border: none;
}

/* --- Styling Tombol & Icon Styling --- */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 500;
  transition: all 0.2s ease-in-out;

  /* Force icon icomoon agar warnanya mengikuti teks button saat di-hover/active */
  [class^='icon-']:before,
  [class*=' icon-']:before {
    color: inherit !important;
  }
}

/* --- Variant Outline Success --- */
.btn-outline-success {
  color: #16a34a !important;
  border-color: #16a34a !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #16a34a !important;
    border-color: #16a34a !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(22, 163, 74, 0.25);
  }
}

/* --- Variant Outline Danger --- */
.btn-outline-danger {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #dc2626 !important;
    border-color: #dc2626 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.25);
  }
}

/* --- Variant Outline Secondary --- */
.btn-outline-secondary {
  color: #4b5563 !important;
  border-color: #d1d5db !important;
  background-color: #ffffff;

  &:hover,
  &:active,
  &.active {
    background-color: #f3f4f6 !important;
    border-color: #9ca3af !important;
    color: #1f2937 !important;
  }
}

.mc-vfoot {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ---------- Kartu Foot Layout (Baris Bertumpuk) ---------- */
.mc-photocard__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 10px; /* <-- beri jarak ke media */
}

/* Foot hanya berisi actions + reasons, bukan title lagi */
.mc-photocard__foot {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
}

.mc-photocard__name {
  font-weight: 700;
  font-size: 14px;
  color: #0f2a5c;
}

.mc-photocard__status-icon {
  font-size: 18px;
}

.mc-photocard__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
}

/* Button Primary Navy Custom Styling */
.btn-primary {
  background-color: #123a86 !important;
  border-color: #123a86 !important;
  color: #ffffff !important;
}

.btn-outline-primary {
  color: #123a86 !important;
  border-color: #123a86 !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #123a86 !important;
    border-color: #123a86 !important;
    color: #ffffff !important;
  }
}

/* ---------- Tombol Zoom (Background Abu-abu & Icon Putih) ---------- */
.mc-photocard__zoom-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(107, 114, 128, 0.85); /* Abu-abu */
  color: #ffffff !important; /* Icon Putih */
  border: none;
  font-size: 14px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  i {
    color: #ffffff !important;
  }

  &:hover {
    background: #4b5563;
    transform: scale(1.08);
  }

  &--sm {
    top: 6px;
    right: 6px;
    width: 26px;
    height: 26px;
    font-size: 11px;
  }
}

/* ---------- Detail SOP Content Alignment ---------- */
/* === Detail card: ubah dari horizontal ke vertikal === */
.mc-detailcard {
  display: flex;
  flex-direction: column; /* <-- dari row ke column */
  align-items: stretch; /* <-- dari center ke stretch */
  gap: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 8px;

  &.is-valid {
    border-color: #22a06b;
  }
  &.is-invalid {
    border-color: #e05a4d;
  }
}

.mc-detailcard__content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* hapus: flex: 1; justify-content: space-between; */
}

.mc-detailcard__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  font-size: 14px;
}

.mc-detailcard__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  margin-top: 4px;
}

/* ---------- Color Variants untuk Button ---------- */
.btn-outline-success {
  color: #16a34a !important;
  border-color: #16a34a !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #16a34a !important;
    border-color: #16a34a !important;
    color: #ffffff !important;
  }
}

.btn-outline-danger {
  color: #dc2626 !important;
  border-color: #dc2626 !important;
  background-color: transparent;

  &:hover,
  &:active,
  &.active {
    background-color: #dc2626 !important;
    border-color: #dc2626 !important;
    color: #ffffff !important;
  }
}

/* ---------- Container & Chip Alasan Tidak Valid ---------- */
.mc-photocard__reasons {
  background-color: #fdecea;
  border: 1px solid #f5c6cb;
}

.mc-reasons__label {
  font-size: 13px;
}

/* Teks counter "X alasan dipilih" warna abu-abu */
.mc-reasons__counter {
  font-size: 12px;
  color: #6b7280 !important;
  font-weight: 500;
}

.mc-reason-chip {
  font-size: 12px !important;
  font-weight: 500;
  border-radius: 999px;
  padding: 4px 12px;

  &.btn-light {
    background-color: #ffffff !important;
    border: 1px solid #e5e7eb !important;
    color: #374151 !important;

    &:hover {
      background-color: #f3f4f6 !important;
      border-color: #d1d5db !important;
    }
  }

  &.btn-danger {
    background-color: #e02424 !important;
    border-color: #e02424 !important;
    color: #ffffff !important;
    box-shadow: 0 2px 4px rgba(224, 36, 36, 0.2);
  }
}

.mc-sticky-header {
  position: sticky;
  top: -16px;
  z-index: 10;
  background: #f6f8fb; /* Menyamakan warna latar modal agar menutup sempurna */
  padding-top: 16px;
  padding-bottom: 4px;
  margin-top: -16px;
}

.mc-svg-icon {
  display: inline-block;
  vertical-align: middle;
  flex-shrink: 0;
}
.mc-svg-icon--status {
  width: 20px;
  height: 20px;
}
.mc-svg-icon--btn {
  width: 14px;
  height: 14px;
}

/* ---------- Preview Modal (Lightbox) ---------- */
.mc-preview-modal .modal-content {
  background: #0f172a;
  border: none;
  border-radius: 12px;
  overflow: hidden;
}
.mc-preview-modal .modal-body {
  padding: 0;
}

.mc-preview-stage {
  position: relative;
  width: 100%;
  height: 78vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0f172a;
  user-select: none;
  touch-action: none;

  &.is-draggable {
    cursor: grab;
  }
  &.is-dragging {
    cursor: grabbing;
  }
}

.mc-preview-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
  user-select: none;
  -webkit-user-drag: none;
  pointer-events: none;
}

/* ---------- Close + Judul sejajar ---------- */
.mc-preview-head {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 12px; /* <-- INI KUNCINYA: bikin width definite */
  z-index: 20;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: none; /* <-- hapus max-width lama */
  pointer-events: none; /* biar klik di area kosong tetap ke stage */
  padding-right: 120px;
}

.mc-preview-close {
  pointer-events: auto; /* <-- balikin clickable untuk tombol close */
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(6px);
  transition: background 0.15s ease;

  svg {
    display: block;
    stroke: currentColor;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.24);
    color: #fff;
  }
}

.mc-preview-title {
  pointer-events: auto; /* <-- biar bisa di-select/hover sendiri */
  min-width: 0; /* penting untuk ellipsis */
  font-size: 13px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(6px);
  padding: 6px 14px;
  border-radius: 999px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  display: inline-block; /* supaya pill lebarnya ikut content */
}

/* ---------- Floating zoom (pojok kanan bawah) ---------- */
.mc-preview-zoom {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 4px;
  background: rgba(17, 24, 39, 0.72);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
  opacity: 0.35;
  transition: opacity 0.2s ease;
}

.mc-preview-stage:hover .mc-preview-zoom {
  opacity: 1;
}

.mc-preview-zoom__btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;

  svg {
    display: block;
    stroke: currentColor;
  }

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &--reset {
    font-size: 13px;
    margin-left: 2px;
    border-left: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 0;
    width: 36px;
  }
}

.mc-preview-zoom__value {
  min-width: 44px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  padding: 0 2px;
  user-select: none;
}

/* Tombol selalu terlihat di mobile */
@media (hover: none) {
  .mc-preview-zoom {
    opacity: 1;
  }
}

.mc-preview-media--video {
  pointer-events: auto;
}
</style>
